import { blinkConstants } from '@thoughtspot/blink-constants';
import { translate } from '@thoughtspot/i18n';
import { areMapTilesEnabled, enableGridlines, getGeoTileSourceId, getMapboxAccessToken, isCustomSortingEnabled, isKPIAnomaliesEnabled, isKPIChartEnhancementsEnabled, smartSortV2Enabled, } from '@thoughtspot/session-service';
import { isAttribute, isColumnValidForNumberFormatting, isDateColumn, isDateFamilyColumn, isDateTimeColumn, isGrowth, isInAnswerFormula, supportsAggregationChange, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { isLegendEnabled } from '../../../components/chart-legend-utils';
import { isShowAnomaliesSupported } from '../../../components/kpi/cortex-utils';
import { getDefaultAbbreviatedPreviousDateFromChartModel, getKpiColumnProperties, isKpiChartTypeTimeseries, } from '../../../components/kpi/kpi-utils';
import { getAxisConfig, getCategoryColumns, getChartSpecificCustomProps, getChartType, getColumnCustomProps, getFormattingForAxis, getLegendColumns, getPivotSummariesState, getRadialColumn, getUseTreeLayout, getVisualizedMeasureColumns, getXAxisColumns, isCustomOrderDefined, isExcludeNullValuesSupported, isFirstYAxisColumn, isHandleMissingValuesConfigurable, isHeatMapOverlayed, isShowNullAsZeroSupported, isSorted, isTimeSeries, isXAxisOrYAxisColumns, isYAxisColumn, isYAxisStackedAsPercent, shouldShowDataLabels, } from '../../../core/chart-model-utils';
import { configOptions, doesSupportHighCardinalityData, isGeoChartType, shouldShowDataLabelToggleForColumn, shouldShowDetailLabelToggleForColumn, } from '../../../core/chart-type-specification-service';
import { getGridLinesConfig } from '../../../core/chart-ui-utils';
import { isConditionalFormattingApplied } from '../../../core/conditional-formatting-utils';
import { containsMeasureNamesOrMeasureValuesColumns } from '../../../core/measure-names-and-values';
import { getLegendCardinality, getSeries } from '../../../data/data-util';
import { hasEmptyRowOrColumn } from '../../../devextreme/components/pivot.component.utils';
import { getEnvFlags } from '../../../env';
import { GEO_TILE_SOURCE_IDS } from '../../../geomap/base/geo-constants';
import { isGradientColoringEnabled } from '../../../highcharts/blink-highchart-config-util';
import { AxisTypes, ChartType, TupleType, } from '../../../types';
import { areGrandTotalsChecked, areSubTotalsChecked, canToggleColumnSummary, getSummaryModeTooltipForSubtotal, isFirstAxis, isFirstAxisOfType, isHeatmapModeEnabled, isLineSeriesColumn, isPivotSummariesDisabled, } from './common-utils';
export const shouldHideAxisSettings = (props) => {
    const { requiredClientStateProperties } = props;
    return !requiredClientStateProperties.visible;
};
export const shouldHideAxisNameSettings = (props) => {
    const { requiredClientStateProperties } = props;
    return !requiredClientStateProperties.isNameVisible;
};
export const shouldHideAxisValueLabelSettings = (props) => {
    const { requiredClientStateProperties } = props;
    return !requiredClientStateProperties.isLabelVisible;
};
export const shouldHideAxisValueLabelNumberFormatter = (props) => {
    const { requiredClientStateProperties, chartModel, currentAxis } = props;
    const formatConfig = getFormattingForAxis(chartModel, currentAxis.id);
    if (!formatConfig) {
        return true;
    }
    return !requiredClientStateProperties.isLabelVisible;
};
export const shouldDisableInheritanceForAxis = (props) => {
    const { chartModel, currentAxis } = props;
    return {
        disabled: isFirstAxis(chartModel, currentAxis),
    };
};
export const shouldDisableInheritanceForDataLabel = (props) => {
    const { chartModel, currentColumn } = props;
    return {
        disabled: isFirstYAxisColumn(chartModel, currentColumn.id),
    };
};
export const shouldDisableGridlineCheckbox = (props) => {
    const { chartModel, currentAxis } = props;
    return {
        disabled: !isFirstAxisOfType(chartModel, currentAxis),
    };
};
export const shouldHideGridLineFromatter = (props) => {
    const { chartModel, currentAxis, requiredClientStateProperties } = props;
    if (_.isNil(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isGridLineEnabled)) {
        const enableGridline = enableGridlines();
        const gridLinesConfig = getGridLinesConfig(chartModel, enableGridline);
        return currentAxis.properties.axisType === AxisTypes.X
            ? !gridLinesConfig.xGridlineEnabled
            : !gridLinesConfig.yGridlineEnabled;
    }
    return (!isFirstAxisOfType(chartModel, currentAxis) ||
        !requiredClientStateProperties.isGridLineEnabled);
};
export const shouldHideIntervalFromatter = (props) => {
    var _a;
    const { requiredClientStateProperties } = props;
    return ((_a = requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.intervals) === null || _a === void 0 ? void 0 : _a.visible) !== true;
};
export const shouldHideRegressionLineFromatter = (props) => {
    var _a;
    const { requiredClientStateProperties } = props;
    return ((_a = requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.regressionLine) === null || _a === void 0 ? void 0 : _a.enabled) !== true;
};
export const shouldHideAxisHorizontalPositionControl = (props) => {
    const { currentAxis } = props;
    return currentAxis.properties.axisType !== AxisTypes.Y;
};
export const shouldHideTableSummarySettings = (props) => {
    const { answerLevelProperty, currentColumn } = props;
    const visibleSummaryColumnIds = answerLevelProperty === null || answerLevelProperty === void 0 ? void 0 : answerLevelProperty.visibleSummaryColumnIds;
    return !(visibleSummaryColumnIds === null || visibleSummaryColumnIds === void 0 ? void 0 : visibleSummaryColumnIds.includes(currentColumn.id));
};
export const shouldHideTableSummaryNumberFormatter = (props) => {
    const { answerLevelProperty, currentColumn } = props;
    const visibleSummaryColumnIds = answerLevelProperty === null || answerLevelProperty === void 0 ? void 0 : answerLevelProperty.visibleSummaryColumnIds;
    const isSummaryVisible = visibleSummaryColumnIds === null || visibleSummaryColumnIds === void 0 ? void 0 : visibleSummaryColumnIds.includes(currentColumn.id);
    return (!isSummaryVisible || !isColumnValidForNumberFormatting(currentColumn));
};
export const shouldHideNullAsZeroCheckbox = (props) => {
    const { chartModel, currentColumn } = props;
    if (!currentColumn)
        return true;
    return !isShowNullAsZeroSupported(chartModel, currentColumn.id);
};
export const shouldHideExcludeNullValuesCheckbox = (props) => {
    const { chartModel, currentColumn } = props;
    if (!currentColumn)
        return true;
    return !isExcludeNullValuesSupported(chartModel, currentColumn.id);
};
export const shouldHideMissingDatesDropdown = (props) => {
    const { chartModel, currentColumn } = props;
    if (!currentColumn)
        return true;
    if (!isHandleMissingValuesConfigurable(chartModel, currentColumn.id)) {
        return true;
    }
    const chartType = getChartType(chartModel);
    if (chartType === ChartType.Kpi) {
        const { showSparkline } = getKpiColumnProperties(chartModel);
        if (!showSparkline) {
            return true;
        }
    }
    return false;
};
export const shouldDisableMissingDatesDropdown = (props) => {
    const { chartModel, currentColumn } = props;
    if (!currentColumn)
        return { disabled: true };
    if (!isTimeSeries(chartModel)) {
        return {
            disabled: true,
            tooltip: translate('charts.columnEditor.HANDLE_MISSING_VALUES_CUSTOM_CALENDAR'),
        };
    }
    return { disabled: false };
};
export const shouldHideLineSeriesSettings = (props) => {
    const { chartModel, currentColumn } = props;
    if (!currentColumn)
        return true;
    return !isLineSeriesColumn(chartModel, currentColumn.id);
};
export const shouldHideColumnColorPicker = (props) => {
    const { chartModel, currentColumn } = props;
    const chartType = getChartType(chartModel);
    const legendColumnIds = getLegendColumns(chartModel).map(col => col.id);
    const categoryColumnIds = getCategoryColumns(chartModel).map(col => col.id);
    const radialColumn = getRadialColumn(chartModel);
    const isRadialColumn = radialColumn && radialColumn.id === currentColumn.id;
    const isSlicedWithColor = legendColumnIds.length > 0;
    switch (chartType) {
        case ChartType.Treemap:
            return (isGradientColoringEnabled() ||
                !isYAxisColumn(chartModel, currentColumn.id));
        case ChartType.LineStackedColumn:
            return (isSlicedWithColor &&
                isFirstYAxisColumn(chartModel, currentColumn.id));
        case ChartType.Bubble:
            if (isSlicedWithColor)
                return true;
            if (isRadialColumn)
                return true;
            if (categoryColumnIds.length > 0) {
                return !isYAxisColumn(chartModel, currentColumn.id);
            }
            return false;
        case ChartType.Scatter:
            if (isSlicedWithColor)
                return true;
            if (categoryColumnIds.length > 0) {
                return !isYAxisColumn(chartModel, currentColumn.id);
            }
            return false;
        case ChartType.Kpi:
            return !isKpiChartTypeTimeseries(chartModel);
        default:
            break;
    }
    return isSlicedWithColor;
};
export const shouldHideColumnDataColorsPicker = (props) => {
    const { chartModel, currentColumn } = props;
    const chartType = getChartType(chartModel);
    const options = configOptions(chartType, chartModel);
    let legendColumnIds = [];
    if (options.allowedConfigurations.configureColorForXAttrs) {
        legendColumnIds = getXAxisColumns(chartModel).map(col => col.id);
    }
    else {
        legendColumnIds = getLegendColumns(chartModel).map(col => col.id);
    }
    const shouldShowColumnDataColorsPicker = legendColumnIds.length > 0 && legendColumnIds[0] === currentColumn.id;
    return !shouldShowColumnDataColorsPicker;
};
export const shouldHideGradientColorPicker = (props) => {
    const { chartModel, currentColumn } = props;
    const chartType = getChartType(chartModel);
    if (chartType === ChartType.GeoArea) {
        if (!isYAxisColumn(chartModel, currentColumn.id))
            return true;
        const legendColumns = getLegendColumns(chartModel);
        return legendColumns.length > 0;
    }
    if (chartType === ChartType.Heatmap) {
        const radialColumn = getRadialColumn(chartModel);
        return (radialColumn === null || radialColumn === void 0 ? void 0 : radialColumn.id) !== currentColumn.id;
    }
    return (!isYAxisColumn(chartModel, currentColumn.id) ||
        !isGradientColoringEnabled());
};
export const shouldDisablePivotConditionalFormatter = (props) => {
    const { chartModel } = props;
    return {
        disabled: isHeatMapOverlayed(chartModel),
        tooltip: translate('conditionalFormat.DISABLE_TOOLTIP_HEATMAP_OVERLAY'),
    };
};
export const shouldHidePivotConditionalFormatter = (props) => {
    const { chartModel, currentColumn } = props;
    const isConditionalFormattingAllowed = isYAxisColumn(chartModel, currentColumn.id) &&
        !isDateColumn(currentColumn);
    return !isConditionalFormattingAllowed;
};
export const shouldHideBubbleConditionalFormatter = (props) => {
    const { chartModel, currentColumn } = props;
    return !isXAxisOrYAxisColumns(currentColumn, chartModel);
};
export const shouldHideMarkerFormatter = (props) => {
    const { chartModel, currentColumn } = props;
    return !isYAxisColumn(chartModel, currentColumn.id);
};
export const shouldDisableDisplaySummaryCheckbox = (props) => {
    const { permission } = props;
    return {
        disabled: !canToggleColumnSummary(permission),
        tooltip: translate('tables.summaries.disable.tooltip'),
    };
};
export const shouldHideAggregationDropdown = (props) => {
    const { currentColumn } = props;
    return isGrowth(currentColumn) || !supportsAggregationChange(currentColumn);
};
export const shouldDisableAggregationDropdown = (props) => {
    const { permission } = props;
    return {
        disabled: !canToggleColumnSummary(permission),
    };
};
export const shouldHideColumnNumberFormatter = (props) => {
    const { currentColumn } = props;
    return !isColumnValidForNumberFormatting(currentColumn);
};
export const shouldHideDataLabelsAccordion = (props) => {
    var _a;
    const { chartModel, currentAxis, currentColumn } = props;
    const chartType = getChartType(chartModel);
    if (chartType === ChartType.Treemap &&
        ((_a = currentAxis === null || currentAxis === void 0 ? void 0 : currentAxis.properties) === null || _a === void 0 ? void 0 : _a.axisType) === 'Y') {
        return false;
    }
    if (chartType === ChartType.Heatmap) {
        return !isYAxisColumn(chartModel, currentColumn.id);
    }
    const shouldShowDataLabel = shouldShowDataLabelToggleForColumn(chartModel, currentColumn.id);
    const shouldShowDetailLabel = shouldShowDetailLabelToggleForColumn(chartModel, currentColumn.id);
    return !shouldShowDataLabel && !shouldShowDetailLabel;
};
export const shouldDisableDataLabelsVisibilityCheckbox = (props) => {
    const { chartModel, chart } = props;
    const maxLabelCount = getEnvFlags().maxLabelCount;
    const defaultBatchSize = getEnvFlags().dataBatchSize || blinkConstants.DEFAULT_DATA_BATCH_SIZE;
    return {
        disabled: !shouldShowDataLabels(chartModel, chart === null || chart === void 0 ? void 0 : chart.getDataModel(), maxLabelCount, defaultBatchSize),
        tooltip: translate('charts.chartEditor.TOO_MANY_LABELS'),
    };
};
export const shouldHideDataLabelSettings = (props) => {
    const { chartModel, requiredClientStateProperties, currentColumn, chart } = props;
    const chartType = getChartType(chartModel);
    const disabledResult = shouldDisableDataLabelsVisibilityCheckbox({
        chartModel,
        chart,
    });
    if (disabledResult.disabled) {
        return true;
    }
    if (chartType === ChartType.Treemap) {
        return (requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isVisible) === false;
    }
    if (chartType === ChartType.Heatmap) {
        return (requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isVisible) === false;
    }
    if (chartType === ChartType.Scatter || chartType === ChartType.Bubble) {
        const categoryColumns = getCategoryColumns(chartModel);
        if (categoryColumns.map(col => col.id).includes(currentColumn.id)) {
            return true;
        }
    }
    if (_.isNil(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isVisible)) {
        const options = configOptions(chartType, chartModel);
        return !(options === null || options === void 0 ? void 0 : options.dataLabelsEnabledByDefault);
    }
    return !(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isVisible);
};
export const shouldHideBarWidthSettings = (props) => {
    const { requiredClientStateProperties } = props;
    return requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.autoSetBarWidth;
};
export const shouldHideSortOrder = (props) => {
    const { currentColumn } = props;
    let isSortFormatOptionEnabled = isCustomSortingEnabled() &&
        isAttribute(currentColumn) &&
        !isDateColumn(currentColumn) &&
        !isDateTimeColumn(currentColumn);
    const isSortV2Enabled = smartSortV2Enabled();
    if (isSortV2Enabled) {
        isSortFormatOptionEnabled =
            isSortFormatOptionEnabled &&
                !isInAnswerFormula(currentColumn) &&
                !isDateFamilyColumn(currentColumn);
    }
    return !isSortFormatOptionEnabled;
};
export const shouldDisableSortOrderRadioGroup = (props) => {
    const { chartModel, currentColumn } = props;
    return {
        disabled: !isSorted(chartModel, currentColumn) ||
            !isCustomOrderDefined(currentColumn),
    };
};
export const shouldHideDataLabelNumericFilter = (props) => {
    var _a, _b, _c, _d, _e;
    const { currentColumn, chartModel } = props;
    const columnProp = (_b = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.columnProperties) === null || _b === void 0 ? void 0 : _b.find(col => col.columnId === (currentColumn === null || currentColumn === void 0 ? void 0 : currentColumn.id));
    const customProps = (_c = columnProp === null || columnProp === void 0 ? void 0 : columnProp.columnProperty) === null || _c === void 0 ? void 0 : _c.customProps;
    const advancedProperties = (_d = JSON.parse(customProps)) === null || _d === void 0 ? void 0 : _d.advancedProperties;
    return !((_e = advancedProperties === null || advancedProperties === void 0 ? void 0 : advancedProperties.dataLabels) === null || _e === void 0 ? void 0 : _e.filter);
};
export const shouldDisableTotalLabelsCheckbox = (props) => {
    const { chartModel } = props;
    const shouldDisableDataLabels = shouldDisableDataLabelsVisibilityCheckbox(props);
    if (shouldDisableDataLabels.disabled) {
        return shouldDisableDataLabels;
    }
    return {
        disabled: isYAxisStackedAsPercent(chartModel),
    };
};
export const shouldHideStackLabelAdvancedSettings = (props) => {
    var _a;
    const { requiredClientStateProperties, chartModel, chart } = props;
    const disabledResult = shouldDisableTotalLabelsCheckbox({
        chartModel,
        chart,
    });
    if (disabledResult.disabled) {
        return true;
    }
    return !((_a = requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.stackLabels) === null || _a === void 0 ? void 0 : _a.isVisible);
};
export const shouldHideStackedAsPercentFormatter = (props) => {
    const { requiredClientStateProperties } = props;
    return !(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isStackedAsPercent);
};
export const shouldHideRegressionLineSection = (props) => {
    const { chart } = props;
    const dataModel = chart === null || chart === void 0 ? void 0 : chart.getDataModel();
    return getSeries(dataModel).length !== 1;
};
export const shouldDisableMapTileDropdown = (props) => {
    const { chartModel } = props;
    const chartType = getChartType(chartModel);
    const isMapboxEnabled = getGeoTileSourceId() === GEO_TILE_SOURCE_IDS.MAPBOX;
    const hasMapTilesEnabled = areMapTilesEnabled();
    const hasMapboxToken = getMapboxAccessToken();
    return {
        disabled: !(isGeoChartType(chartType) &&
            isMapboxEnabled &&
            hasMapTilesEnabled &&
            hasMapboxToken),
    };
};
export const shouldDisableDisplayValuesRadioGroup = (props) => {
    const { chartModel } = props;
    const axisConfig = getAxisConfig(chartModel);
    const areMeasureNamesOrMeasureValuesColumnPresent = containsMeasureNamesOrMeasureValuesColumns(axisConfig);
    return {
        disabled: areMeasureNamesOrMeasureValuesColumnPresent,
        tooltip: translate('charts.chartEditor.pivot.disabledMeasureLayout.tooltipContent'),
    };
};
export const shouldDisableHeatmapModeCheckbox = (props) => {
    const { chartModel } = props;
    const visualizedMeasureColumns = getVisualizedMeasureColumns(chartModel);
    const isPivotConditionalFormattingApplied = isConditionalFormattingApplied(chartModel.vizProp, visualizedMeasureColumns, 'ChartVizProps', chartModel);
    return {
        disabled: isPivotConditionalFormattingApplied,
        tooltip: translate('charts.chartEditor.HEATMAP_DISALLOW_CF_APPLIED'),
    };
};
export const shouldDisableShowSubtotals = (props) => {
    const { chartModel, requiredClientStateProperties } = props;
    const axisConfig = getAxisConfig(chartModel);
    const isTreeLayoutEnabled = getUseTreeLayout(chartModel);
    const heatmapModeEnabled = isHeatmapModeEnabled(axisConfig, requiredClientStateProperties);
    const { disabled: pivotSummariesDisabled, tooltip: pivotSummariesDisabledTooltip, } = isPivotSummariesDisabled(props);
    const summaryModeTooltip = getSummaryModeTooltipForSubtotal(chartModel, requiredClientStateProperties);
    const treeLayoutTooltip = isTreeLayoutEnabled
        ? translate('charts.chartEditor.pivotTable.tooltip.ENFORCE_TO_ENABLE_SUBTOTAL_ROW_SUMMARIES')
        : '';
    const finalTooltip = summaryModeTooltip ||
        pivotSummariesDisabledTooltip ||
        treeLayoutTooltip;
    return {
        disabled: pivotSummariesDisabled || heatmapModeEnabled,
        tooltip: finalTooltip,
    };
};
export const shouldDisableTotalsPositionRadioGroup = isPivotSummariesDisabled;
export const shouldDisableShowTotalsCheckbox = isPivotSummariesDisabled;
export const shouldDisableColumnSubtotalsCheckbox = isPivotSummariesDisabled;
export const shouldDisableRowSubtotalsCheckbox = shouldDisableShowSubtotals;
export const shouldDisableDisplayValueFormatDropdown = (props) => {
    const { chartModel } = props;
    const pivotSummariesState = getPivotSummariesState(chartModel);
    const axisConfig = getAxisConfig(chartModel);
    const enforceToShowSummaries = hasEmptyRowOrColumn(axisConfig);
    const areMeasureNamesOrMeasureValuesColumnPresent = containsMeasureNamesOrMeasureValuesColumns(axisConfig);
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    const isGrandTotalCheckboxChecked = areGrandTotalsChecked(pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, chartConfigOptions);
    const summariesEnabled = isGrandTotalCheckboxChecked &&
        areSubTotalsChecked(pivotSummariesState, enforceToShowSummaries, areMeasureNamesOrMeasureValuesColumnPresent, chartConfigOptions);
    return {
        disabled: !summariesEnabled,
        tooltip: translate('charts.chartEditor.pivotTable.tooltip.SUMMARY_MODE_DISABLED'),
    };
};
export const shouldHideComparisonSettings = (props) => {
    const { requiredClientStateProperties } = props;
    return !(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.showComparison);
};
export const shouldHideCustomComparisonInput = (props) => {
    var _a, _b, _c, _d;
    if (!isKPIChartEnhancementsEnabled()) {
        const { requiredClientStateProperties } = props;
        return (!(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.showComparison) ||
            (requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.customCompare) !== TupleType.Custom);
    }
    const { currentColumn, chartModel } = props;
    const columnProp = (_b = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.columnProperties) === null || _b === void 0 ? void 0 : _b.find(col => col.columnId === (currentColumn === null || currentColumn === void 0 ? void 0 : currentColumn.id));
    const comparisonSettings = (_c = columnProp === null || columnProp === void 0 ? void 0 : columnProp.columnProperty) === null || _c === void 0 ? void 0 : _c.kpiColumnProperties;
    const columnCustomProps = getColumnCustomProps(chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp, currentColumn === null || currentColumn === void 0 ? void 0 : currentColumn.id);
    const chartSpecificCustomProps = getChartSpecificCustomProps(chartModel);
    const customCompareValue = (_d = columnCustomProps === null || columnCustomProps === void 0 ? void 0 : columnCustomProps.customCompare) !== null && _d !== void 0 ? _d : chartSpecificCustomProps === null || chartSpecificCustomProps === void 0 ? void 0 : chartSpecificCustomProps.customCompare;
    return ((comparisonSettings === null || comparisonSettings === void 0 ? void 0 : comparisonSettings.showComparisonDate) === false ||
        customCompareValue !== TupleType.Custom);
};
export const shouldHideShowAbbreviatedPreviousDateCheckbox = (props) => {
    var _a, _b, _c;
    const { currentColumn, chartModel } = props;
    const columnProp = (_b = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.columnProperties) === null || _b === void 0 ? void 0 : _b.find(col => col.columnId === (currentColumn === null || currentColumn === void 0 ? void 0 : currentColumn.id));
    const comparisonSettings = (_c = columnProp === null || columnProp === void 0 ? void 0 : columnProp.columnProperty) === null || _c === void 0 ? void 0 : _c.kpiColumnProperties;
    return (comparisonSettings === null || comparisonSettings === void 0 ? void 0 : comparisonSettings.showComparisonDate) === false;
};
export const shouldHideShowAbbreviatedPreviousDateInput = (props) => {
    var _a, _b, _c;
    const { currentColumn, chartModel } = props;
    const columnProp = (_b = (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.columnProperties) === null || _b === void 0 ? void 0 : _b.find(col => col.columnId === (currentColumn === null || currentColumn === void 0 ? void 0 : currentColumn.id));
    const comparisonSettings = (_c = columnProp === null || columnProp === void 0 ? void 0 : columnProp.columnProperty) === null || _c === void 0 ? void 0 : _c.kpiColumnProperties;
    return ((comparisonSettings === null || comparisonSettings === void 0 ? void 0 : comparisonSettings.showComparisonDate) === false ||
        !(comparisonSettings === null || comparisonSettings === void 0 ? void 0 : comparisonSettings.showAbbreviatedPreviousDate));
};
export const shouldShowAbbreviatedPreviousDateError = (chartModel) => {
    if (!chartModel)
        return false;
    const props = getKpiColumnProperties(chartModel);
    if ((props === null || props === void 0 ? void 0 : props.showAbbreviatedPreviousDate) === false)
        return false;
    const savedValue = props.abbreviatedPreviousDate;
    const defaultValue = getDefaultAbbreviatedPreviousDateFromChartModel(chartModel);
    const isExplicitlyCleared = savedValue === '';
    const isMissingAndNoDefault = savedValue === undefined && !defaultValue;
    return isExplicitlyCleared || isMissingAndNoDefault;
};
export const shouldHideAnomalyColorPicker = (props) => {
    var _a;
    const { requiredClientStateProperties, chartModel } = props;
    return (!((_a = requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.showAnomalies) !== null && _a !== void 0 ? _a : true) ||
        !isShowAnomaliesSupported(chartModel));
};
export const shouldHideSparklineColorPicker = (props) => {
    const { currentColumn, chartModel } = props;
    if (!currentColumn)
        return true;
    const kpiColumnProperties = getKpiColumnProperties(chartModel);
    return (kpiColumnProperties === null || kpiColumnProperties === void 0 ? void 0 : kpiColumnProperties.showSparkline) === false;
};
export const shouldHideAnomalySettings = (props) => {
    const { requiredClientStateProperties } = props;
    return (!isKPIAnomaliesEnabled() ||
        (requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.showSparkline) === false);
};
export const shouldDisableShowAnomalyCheckbox = (props) => {
    const { chartModel } = props;
    return {
        disabled: !isShowAnomaliesSupported(chartModel),
        tooltip: translate('charts.chartEditor.KPI.ANOMALIES_UNSUPPORTED_INFO_TEXT'),
    };
};
export const shouldDisableMaxDataPointsInput = (props) => {
    const { chartModel } = props;
    return {
        disabled: !doesSupportHighCardinalityData(getChartType(chartModel)),
    };
};
export const shouldHideChangeAsDropdown = (props) => {
    var _a;
    const { requiredClientStateProperties } = props;
    return !((_a = requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.kpiDisplayProperties) === null || _a === void 0 ? void 0 : _a.showChange);
};
export const shouldHideTooltipSettings = (props) => {
    const { requiredClientStateProperties } = props;
    return !(requiredClientStateProperties === null || requiredClientStateProperties === void 0 ? void 0 : requiredClientStateProperties.isVisible);
};
export const shouldDisableTooltipVisibilityCheckbox = (props) => {
    const { chartModel } = props;
    const chartType = getChartType(chartModel);
    const disabledChartTypes = [
        ChartType.GeoArea,
        ChartType.GeoHeatmap,
        ChartType.GeoBubble,
        ChartType.Sankey,
    ];
    if (disabledChartTypes.includes(chartType)) {
        return {
            disabled: true,
            tooltip: translate('chartSettingsV2.disabledTooltipVisibilityCheckbox'),
        };
    }
    return {
        disabled: false,
    };
};
export const shouldDisableLegendCheckbox = (props) => {
    const { chartModel, chart } = props;
    const dataModel = chart === null || chart === void 0 ? void 0 : chart.getDataModel();
    const isDisabled = !!dataModel &&
        getLegendCardinality(chartModel, dataModel) <= 1;
    if (isDisabled) {
        return {
            disabled: true,
            tooltip: translate('chartSettingsV2.disabledLegendTooltip'),
        };
    }
    return {
        disabled: false,
    };
};
export const shouldDisableLegendPosition = (props) => {
    const { chartModel, chart } = props;
    const dataModel = chart === null || chart === void 0 ? void 0 : chart.getDataModel();
    const isLegendDisabled = !dataModel ||
        getLegendCardinality(chartModel, dataModel) <= 1;
    const legendEnabled = isLegendEnabled(chartModel);
    const isDisabled = isLegendDisabled || !(_.isNil(legendEnabled) || legendEnabled);
    if (isDisabled) {
        return {
            disabled: true,
            tooltip: translate('chartSettingsV2.disabledLegendTooltip'),
        };
    }
    return {
        disabled: false,
    };
};
export const shouldHideShowNullDataAsDropdown = (props) => {
    const { chartModel } = props;
    return !isKPIChartEnhancementsEnabled() || !isKpiChartTypeTimeseries(chartModel);
};
//# sourceMappingURL=common-rules.js.map